import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { equipmentService } from '../../services/equipment';
import type { EquipmentTypeOption } from '../../types/equipment';

interface EquipmentTypePickerProps {
  /** The stored type_key value (e.g. 'airblast_sprayer' or 'custom') */
  value: string;
  /** The display label — used when type_key is a custom entry */
  customLabel: string;
  /**
   * `option` is the registry row picked — its category and the defaults a new
   * machine of that type starts with. Absent for a brand-new custom type,
   * which has no row until the machine is saved.
   */
  onChange: (typeKey: string, customLabel: string, option?: EquipmentTypeOption) => void;
  error?: string;
}

/**
 * Equipment type combobox.
 *
 * - Loads built-in + org-specific custom types from the API.
 * - Filtering as you type narrows the list.
 * - If you type something that doesn't match any existing type, an "Add …" option
 *   appears — selecting it stores it as a custom type.
 * - Custom types are saved to the DB when the equipment is saved (via createEquipment /
 *   updateEquipment), so the registry grows organically without sprawl.
 */
function EquipmentTypePicker({ value, customLabel, onChange, error }: EquipmentTypePickerProps) {
  const [types, setTypes] = useState<EquipmentTypeOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load types from API
  useEffect(() => {
    equipmentService.getTypes()
      .then(res => {
        if (res.success && res.data) setTypes(res.data);
      })
      .catch(() => {
        // Fallback to built-ins if API fails
        setTypes([
          { type_key: 'airblast_sprayer', label: 'Airblast Sprayer', is_builtin: true },
          { type_key: 'boom_sprayer',     label: 'Boom Sprayer',      is_builtin: true },
          { type_key: 'knapsack',         label: 'Knapsack',          is_builtin: true },
          { type_key: 'drone',            label: 'Drone',             is_builtin: true },
          { type_key: 'vehicle',          label: 'Vehicle',           is_builtin: true },
          { type_key: 'other',            label: 'Other',             is_builtin: true },
        ]);
      })
      .finally(() => setLoading(false));
  }, []);

  // Sync input display when value changes externally
  useEffect(() => {
    if (!value) {
      setInputValue('');
      return;
    }
    if (value === 'custom') {
      setInputValue(customLabel || '');
      return;
    }
    const found = types.find(t => t.type_key === value);
    setInputValue(found ? found.label : value);
  }, [value, customLabel, types]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = inputValue.trim()
    ? types.filter(t => t.label.toLowerCase().includes(inputValue.toLowerCase()))
    : types;

  const exactMatch = types.some(
    t => t.label.toLowerCase() === inputValue.trim().toLowerCase()
  );
  const showAddOption = inputValue.trim().length >= 2 && !exactMatch;

  const selectType = (t: EquipmentTypeOption) => {
    setInputValue(t.label);
    onChange(t.type_key, '', t);
    setOpen(false);
  };

  const addCustom = () => {
    const label = inputValue.trim();
    const typeKey = 'custom';
    onChange(typeKey, label);
    // Optimistically add to local list so it appears in future sessions before page reload
    if (!types.some(t => t.label.toLowerCase() === label.toLowerCase())) {
      setTypes(prev => [
        ...prev,
        { type_key: label.toLowerCase().replace(/\s+/g, '_'), label, is_builtin: false },
      ]);
    }
    setOpen(false);
  };

  const displayLabel = () => {
    if (!value) return '';
    if (value === 'custom') return customLabel;
    const found = types.find(t => t.type_key === value);
    return found ? found.label : value;
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`flex items-center border rounded-lg bg-white transition-shadow ${
          open ? 'ring-2 ring-primary-500 border-primary-500' : error ? 'border-red-300' : 'border-neutral-300'
        }`}
      >
        <input
          type="text"
          value={open ? inputValue : (displayLabel() || inputValue)}
          onChange={e => {
            setInputValue(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setInputValue('');
            setOpen(true);
          }}
          placeholder={loading ? 'Loading types…' : 'Select or type a type…'}
          disabled={loading}
          className="flex-1 px-3 py-2 text-sm bg-transparent outline-none rounded-lg disabled:opacity-60"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setOpen(o => !o)}
          className="px-2 text-neutral-400 hover:text-neutral-600"
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {/* Built-in types */}
          {filtered.filter(t => t.is_builtin).length > 0 && (
            <>
              <div className="px-3 py-1 text-xs font-semibold text-neutral-400 uppercase tracking-wide bg-neutral-50">
                Standard
              </div>
              {filtered.filter(t => t.is_builtin).map(t => (
                <button
                  key={t.type_key}
                  type="button"
                  onClick={() => selectType(t)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 hover:text-primary-700 transition-colors ${
                    value === t.type_key ? 'bg-primary-50 text-primary-700 font-medium' : 'text-neutral-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </>
          )}

          {/* Org-specific custom types */}
          {filtered.filter(t => !t.is_builtin).length > 0 && (
            <>
              <div className="px-3 py-1 text-xs font-semibold text-neutral-400 uppercase tracking-wide bg-neutral-50 border-t border-neutral-100">
                Custom
              </div>
              {filtered.filter(t => !t.is_builtin).map(t => (
                <button
                  key={t.type_key}
                  type="button"
                  onClick={() => selectType(t)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 hover:text-primary-700 transition-colors ${
                    (value === t.type_key || (value === 'custom' && customLabel?.toLowerCase() === t.label.toLowerCase()))
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-neutral-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </>
          )}

          {/* Add new custom type */}
          {showAddOption && (
            <button
              type="button"
              onClick={addCustom}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 border-t border-neutral-100 transition-colors"
            >
              <Plus className="h-4 w-4 flex-shrink-0" />
              Add &ldquo;{inputValue.trim()}&rdquo; as new type
            </button>
          )}

          {filtered.length === 0 && !showAddOption && (
            <div className="px-3 py-3 text-sm text-neutral-400 text-center">
              No matches — keep typing to add a new type
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default EquipmentTypePicker;
